import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ClipboardList, Rocket, Plus, FileSpreadsheet, Printer, HelpCircle, Settings2,
  CheckCircle2, Loader2, AlertTriangle, Clock, CircleDot, Ban, Flag, ChevronRight, Trophy, RotateCcw,
} from 'lucide-react';
import { TaskDetailDrawer } from '../../components/onboarding/TaskDetailDrawer';
import { PlannerFaqDrawer } from '../../components/onboarding/PlannerFaqDrawer';
import { AddTaskDialog } from '../../components/onboarding/AddTaskDialog';
import { PlannerPrintView } from '../../components/onboarding/PlannerPrintView';
import {
  PRIORITY_STYLES, STATUS_STYLES, PHASE_META, fmtDate,
} from '../../components/onboarding/plannerConstants';

const StatCard = ({ icon: Icon, label, value, tint, testid }) => (
  <div className="rounded-lg border border-border bg-card px-4 py-3 flex items-center gap-3" data-testid={testid}>
    <span className={`flex h-9 w-9 items-center justify-center rounded-md ${tint}`}><Icon className="h-4 w-4" /></span>
    <div>
      <div className="text-xl font-bold leading-none text-foreground">{value}</div>
      <div className="text-[11px] text-muted-foreground mt-1">{label}</div>
    </div>
  </div>
);

const OnboardingPlannerPage = () => {
  const navigate = useNavigate();
  const [currentAccount, setCurrentAccount] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [drawerTask, setDrawerTask] = useState(null);
  const [faqOpen, setFaqOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [startForm, setStartForm] = useState({ plan_start_date: new Date().toISOString().slice(0, 10), target_go_live_date: '' });
  const [settingsForm, setSettingsForm] = useState({ target_go_live_date: '', plan_start_date: '', management_update: '' });

  useEffect(() => {
    (async () => {
      try {
        const { data: accounts } = await api.get('/auth/accounts');
        const stored = localStorage.getItem('current_account');
        const storedAccount = stored ? JSON.parse(stored) : null;
        let account = storedAccount ? accounts.find((a) => a.id === storedAccount.id) : null;
        if (!account && accounts.length) account = accounts[0];
        if (!account) { navigate('/dashboard'); return; }
        localStorage.setItem('current_account', JSON.stringify(account));
        if (!account.enabled_apps?.ubm) { navigate('/dashboard'); return; }
        setCurrentAccount(account);
        setInitializing(false);
      } catch {
        navigate('/dashboard');
      }
    })();
  }, [navigate]);

  const load = useCallback(async () => {
    if (!currentAccount?.id) return;
    setLoading(true);
    try {
      const { data: d } = await api.get('/onboarding-planner/plan', { params: { account_id: currentAccount.id } });
      setData(d);
      setSettingsForm({
        target_go_live_date: (d.plan.target_go_live_date || '').slice(0, 10),
        plan_start_date: (d.plan.plan_start_date || '').slice(0, 10),
        management_update: d.plan.management_update || '',
      });
      // keep drawer task in sync after refetch
      setDrawerTask((prev) => {
        if (!prev) return prev;
        for (const g of d.groups) { const f = g.tasks.find((t) => t.id === prev.id); if (f) return f; }
        return prev;
      });
    } catch (e) {
      toast.error('Could not load the onboarding plan');
    } finally {
      setLoading(false);
    }
  }, [currentAccount]);

  useEffect(() => { load(); }, [load]);

  const doStart = async () => {
    try {
      await api.post('/onboarding-planner/plan/start', startForm, { params: { account_id: currentAccount.id } });
      toast.success('Onboarding project started');
      setStartOpen(false);
      load();
    } catch { toast.error('Could not start the project'); }
  };

  const saveSettings = async () => {
    try {
      await api.put('/onboarding-planner/plan', settingsForm, { params: { account_id: currentAccount.id } });
      toast.success('Plan updated');
      setSettingsOpen(false);
      load();
    } catch { toast.error('Could not update plan'); }
  };

  const completePlan = async () => {
    if (!window.confirm('Mark the entire onboarding plan as Completed? The planner will move to the bottom of the UBM Onboarding menu.')) return;
    try {
      await api.post('/onboarding-planner/plan/complete', {}, { params: { account_id: currentAccount.id } });
      toast.success('Onboarding marked complete 🎉');
      window.dispatchEvent(new CustomEvent('planner-status-changed', { detail: { completed: true } }));
      load();
    } catch { toast.error('Could not complete the plan'); }
  };

  const reopenPlan = async () => {
    try {
      await api.post('/onboarding-planner/plan/reopen', {}, { params: { account_id: currentAccount.id } });
      toast.success('Plan reopened');
      window.dispatchEvent(new CustomEvent('planner-status-changed', { detail: { completed: false } }));
      load();
    } catch { toast.error('Could not reopen the plan'); }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get('/onboarding-planner/export.xlsx', {
        params: { account_id: currentAccount.id }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(data?.org_name || 'organisation').replace(/\s/g, '_')}_onboarding_plan.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const doPrint = () => {
    setPrintOpen(true);
    setTimeout(() => { window.print(); }, 300);
  };

  if (initializing) {
    return <MainLayout><div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></MainLayout>;
  }

  const plan = data?.plan;
  const progress = data?.progress || {};
  const notStarted = plan?.status === 'Not Started';
  const completed = plan?.status === 'Completed';

  return (
    <MainLayout wide>
      <div className="max-w-[1400px] mx-auto" data-testid="onboarding-planner-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="flex items-center gap-2 text-primary">
              <ClipboardList className="h-5 w-5" />
              <h1 className="text-2xl font-bold text-foreground">Onboarding Planner</h1>
              {completed && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Completed</Badge>}
              {plan?.status === 'Active' && <Badge className="bg-blue-100 text-blue-800 border-blue-200">Active</Badge>}
            </div>
            <p className="text-sm text-muted-foreground mt-1">{data?.org_name} · {plan?.name}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {notStarted && (
              <Button onClick={() => setStartOpen(true)} data-testid="start-project-btn">
                <Rocket className="h-4 w-4 mr-1.5" /> Start Project
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} data-testid="add-task-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Task</Button>
            <Button variant="outline" size="sm" onClick={exportExcel} data-testid="export-excel-btn"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Export Excel</Button>
            <Button variant="outline" size="sm" onClick={doPrint} data-testid="print-pdf-btn"><Printer className="h-4 w-4 mr-1.5" /> Print / PDF</Button>
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="plan-settings-btn"><Settings2 className="h-4 w-4 mr-1.5" /> Plan Settings</Button>
            <Button variant="outline" size="sm" onClick={() => setFaqOpen(true)} data-testid="faq-btn"><HelpCircle className="h-4 w-4 mr-1.5" /> FAQs</Button>
          </div>
        </div>

        {/* Dashboard summary */}
        <div className="rounded-xl border border-border bg-gradient-to-br from-muted/40 to-card p-5 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-5 items-center">
            <div className="lg:col-span-1">
              <div className="text-4xl font-bold text-foreground" data-testid="overall-progress">{progress.overall_pct ?? 0}%</div>
              <div className="text-xs text-muted-foreground mb-2">Overall completion</div>
              <Progress value={progress.overall_pct ?? 0} className="h-2" />
              <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                <span>Start: <strong className="text-foreground">{fmtDate(plan?.plan_start_date)}</strong></span>
                <span>Target Go-Live: <strong className="text-foreground">{fmtDate(plan?.target_go_live_date)}</strong></span>
              </div>
            </div>
            <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
              <StatCard icon={CheckCircle2} label="Completed" value={progress.completed ?? 0} tint="bg-emerald-100 text-emerald-700" testid="stat-completed" />
              <StatCard icon={CircleDot} label="In Progress" value={progress.in_progress ?? 0} tint="bg-blue-100 text-blue-700" testid="stat-inprogress" />
              <StatCard icon={Clock} label="Pending" value={progress.pending ?? 0} tint="bg-slate-100 text-slate-600" testid="stat-pending" />
              <StatCard icon={Ban} label="Blocked" value={progress.blocked ?? 0} tint="bg-rose-100 text-rose-700" testid="stat-blocked" />
              <StatCard icon={AlertTriangle} label="Overdue" value={progress.overdue ?? 0} tint="bg-amber-100 text-amber-700" testid="stat-overdue" />
            </div>
          </div>
          {plan?.status === 'Active' && (
            <div className="mt-4 flex justify-end">
              <Button size="sm" onClick={completePlan} data-testid="complete-plan-btn"><Trophy className="h-4 w-4 mr-1.5" /> Mark Onboarding Complete</Button>
            </div>
          )}
          {completed && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Onboarding completed on {fmtDate(plan.actual_go_live_date)}. This plan is retained as your implementation record.</p>
              <Button size="sm" variant="outline" onClick={reopenPlan} data-testid="reopen-plan-btn"><RotateCcw className="h-4 w-4 mr-1.5" /> Reopen Plan</Button>
            </div>
          )}
        </div>

        {/* Task groups */}
        {loading && !data ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-6">
            {(data?.groups || []).map((g) => {
              const meta = PHASE_META[g.phase] || PHASE_META.Custom;
              return (
                <div key={g.phase} data-testid={`phase-${g.phase.replace(/\s/g, '-').toLowerCase()}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
                    <h2 className={`text-sm font-semibold ${meta.tint}`}>{g.phase}</h2>
                    <span className="text-xs text-muted-foreground">({g.tasks.length})</span>
                  </div>
                  <div className="rounded-lg border border-border bg-card overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40 text-[11px] uppercase text-muted-foreground">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium">Task</th>
                          <th className="text-left px-3 py-2 font-medium w-28">Progress</th>
                          <th className="text-left px-3 py-2 font-medium w-40">Assigned To</th>
                          <th className="text-left px-3 py-2 font-medium w-24">Priority</th>
                          <th className="text-left px-3 py-2 font-medium w-28">Start</th>
                          <th className="text-left px-3 py-2 font-medium w-28">End</th>
                          <th className="text-left px-3 py-2 font-medium w-32">Status</th>
                          <th className="w-8" />
                        </tr>
                      </thead>
                      <tbody>
                        {g.tasks.map((t) => {
                          const owner = (data.members.find((m) => m.id === t.assigned_user_id) || {}).name;
                          return (
                            <tr key={t.id} className="border-t border-border hover:bg-muted/30 cursor-pointer transition-colors"
                              onClick={() => setDrawerTask(t)} data-testid={`task-row-${t.id}`}>
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-foreground">{t.title}</span>
                                  {t.is_custom && <Badge variant="outline" className="text-[9px]">Custom</Badge>}
                                  {t.overdue && <Badge className="text-[9px] bg-amber-100 text-amber-800 border-amber-200">Overdue</Badge>}
                                  {t.update_count > 0 && <span className="text-[10px] text-muted-foreground">💬 {t.update_count}</span>}
                                </div>
                              </td>
                              <td className="px-3 py-2.5">
                                {t.volume_info?.enabled ? (
                                  <span className="font-mono text-xs font-semibold text-foreground" data-testid={`task-vol-${t.id}`}>{t.volume_info.display || '—'}</span>
                                ) : <span className="text-muted-foreground text-xs">—</span>}
                              </td>
                              <td className="px-3 py-2.5 text-xs">{owner || <span className="text-muted-foreground">Unassigned</span>}</td>
                              <td className="px-3 py-2.5"><Badge variant="outline" className={`text-[10px] ${PRIORITY_STYLES[t.priority] || ''}`}><Flag className="h-2.5 w-2.5 mr-1" />{t.priority}</Badge></td>
                              <td className="px-3 py-2.5 text-xs text-muted-foreground">{fmtDate(t.planned_start_date)}</td>
                              <td className="px-3 py-2.5 text-xs text-muted-foreground">{fmtDate(t.planned_end_date)}</td>
                              <td className="px-3 py-2.5"><Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[t.status] || ''}`}>{t.status}</Badge></td>
                              <td className="px-2"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Drawers & dialogs */}
      <TaskDetailDrawer open={!!drawerTask} onOpenChange={(o) => !o && setDrawerTask(null)}
        task={drawerTask} accountId={currentAccount?.id} members={data?.members || []}
        navigate={navigate} onChanged={load} planCompleted={completed} />
      <PlannerFaqDrawer open={faqOpen} onOpenChange={setFaqOpen} />
      <AddTaskDialog open={addOpen} onOpenChange={setAddOpen} accountId={currentAccount?.id} members={data?.members || []} onCreated={load} />

      {/* Start Project dialog */}
      <Dialog open={startOpen} onOpenChange={setStartOpen}>
        <DialogContent data-testid="start-project-dialog">
          <DialogHeader>
            <DialogTitle>Start Onboarding Project</DialogTitle>
            <DialogDescription>Set your plan start date and target go-live. You can change these later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Plan Start Date</Label>
              <Input type="date" value={startForm.plan_start_date} onChange={(e) => setStartForm({ ...startForm, plan_start_date: e.target.value })} data-testid="start-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Target Go-Live Date</Label>
              <Input type="date" value={startForm.target_go_live_date} onChange={(e) => setStartForm({ ...startForm, target_go_live_date: e.target.value })} data-testid="target-date-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStartOpen(false)}>Cancel</Button>
            <Button onClick={doStart} data-testid="confirm-start-btn"><Rocket className="h-4 w-4 mr-1.5" /> Start Project</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="plan-settings-dialog">
          <DialogHeader>
            <DialogTitle>Plan Settings</DialogTitle>
            <DialogDescription>Update key dates and the management summary shown on the printed plan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Plan Start Date</Label>
                <Input type="date" value={settingsForm.plan_start_date} onChange={(e) => setSettingsForm({ ...settingsForm, plan_start_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Target Go-Live Date</Label>
                <Input type="date" value={settingsForm.target_go_live_date} onChange={(e) => setSettingsForm({ ...settingsForm, target_go_live_date: e.target.value })} data-testid="settings-target-date" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Management Summary (shown on PDF)</Label>
              <Textarea rows={4} value={settingsForm.management_update} onChange={(e) => setSettingsForm({ ...settingsForm, management_update: e.target.value })} data-testid="settings-management-update" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={saveSettings} data-testid="save-settings-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {printOpen && <PlannerPrintView data={data} includeNotesNarrative />}
    </MainLayout>
  );
};

export default OnboardingPlannerPage;
